import { useState } from 'react';
import {
  THEME_TAGS,
  THEME_TAG_CATEGORY_LABELS,
  THEME_TAG_CATEGORY_HINTS,
  type ThemeTagCategory,
} from '@/lib/themeTags';

const CATEGORY_ORDER: ThemeTagCategory[] = ['simple', 'block', 'marketplace'];

export default function ThemeTagsReference() {
  const [copied, setCopied] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const handleCopy = (text: string) => {
    try {
      navigator.clipboard?.writeText(text);
      setCopied(text);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      /* clipboard indisponible */
    }
  };

  const filtered = THEME_TAGS.filter((t) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return t.name.toLowerCase().includes(q) || t.description.toLowerCase().includes(q);
  });

  return (
    <div className="overflow-y-auto h-full p-5">
      {/* En-tête */}
      <div className="mb-5 p-4 rounded-lg bg-accent-50 border border-accent-100">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-accent-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-book-open-line text-accent-600"></i>
          </div>
          <div>
            <h3 className="text-sm font-bold text-foreground-950">Référence des template tags</h3>
            <p className="text-xs text-foreground-600 mt-1 leading-relaxed">
              Insérez ces balises dans le contenu HTML des pages du thème. Le moteur de Zifek les
              remplace automatiquement par les données réelles du commerce. N&apos;inventez pas de
              nouvelle balise : utilisez uniquement celles listées ci-dessous.
            </p>
          </div>
        </div>
      </div>

      {/* Recherche */}
      <div className="relative mb-5 max-w-md">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher une balise..."
          className="w-full pl-9 pr-3 h-10 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
        />
      </div>

      {/* Catégories */}
      <div className="space-y-6">
        {CATEGORY_ORDER.map((category) => {
          const tags = filtered.filter((t) => t.category === category);
          if (tags.length === 0) return null;
          return (
            <div key={category}>
              <div className="mb-3">
                <h4 className="text-xs font-bold uppercase tracking-wider text-foreground-700">
                  {THEME_TAG_CATEGORY_LABELS[category]}
                  <span className="ml-2 text-[10px] font-medium text-foreground-400 normal-case tracking-normal">
                    ({tags.length})
                  </span>
                </h4>
                <p className="text-[11px] text-foreground-400 mt-0.5">{THEME_TAG_CATEGORY_HINTS[category]}</p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {tags.map((tag) => (
                  <div
                    key={tag.name}
                    className="bg-background-50 border border-background-200/70 rounded-lg p-3.5 hover:border-background-300/60 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-foreground-900 font-mono truncate">{tag.name}</p>
                        <p className="text-[11px] text-foreground-500 mt-0.5 leading-snug">{tag.description}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleCopy(tag.usage)}
                        className={`flex-shrink-0 flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium whitespace-nowrap transition-colors cursor-pointer ${
                          copied === tag.usage
                            ? 'bg-accent-100 text-accent-700'
                            : 'bg-background-100 text-foreground-500 hover:bg-background-200/70 hover:text-foreground-800'
                        }`}
                      >
                        <i className={copied === tag.usage ? 'ri-check-line' : 'ri-file-copy-line'}></i>
                        {copied === tag.usage ? 'Copié' : 'Copier'}
                      </button>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="text-[11px] font-mono px-2 py-1 rounded bg-background-100 text-primary-700 border border-background-200/70">
                        {tag.usage}
                      </code>
                      {tag.supportsLimit && (
                        <span className="text-[10px] text-foreground-400">accepte l&apos;attribut limit=&quot;N&quot;</span>
                      )}
                      {tag.extraAttrs?.map((attr) => (
                        <span key={attr} className="text-[10px] text-foreground-400">
                          attribut {attr}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {filtered.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-search-line text-3xl text-foreground-300 mb-2 block"></i>
            <p className="text-sm text-foreground-500">Aucune balise ne correspond à votre recherche</p>
          </div>
        )}
      </div>

      {/* Bonnes pratiques */}
      <div className="mt-6 p-4 rounded-lg bg-background-100/70 border border-background-200/70">
        <h4 className="text-xs font-bold uppercase tracking-wider text-foreground-700 mb-2">Bonnes pratiques</h4>
        <ul className="space-y-1.5 text-[11px] text-foreground-500 leading-relaxed">
          <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5"></i>Aucun texte descriptif en dur : utilisez les balises pour que le marchand puisse modifier son contenu.</li>
          <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5"></i>Une page <code className="font-mono text-foreground-700">head</code>, <code className="font-mono text-foreground-700">header</code>, <code className="font-mono text-foreground-700">navmenu</code> et <code className="font-mono text-foreground-700">footer</code> sont obligatoires : elles structurent tout le site.</li>
          <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5"></i>Aucun JavaScript ni appel réseau dans les pages : le moteur Zifek gère le rendu dynamique.</li>
          <li className="flex items-start gap-2"><i className="ri-check-line text-accent-500 mt-0.5"></i>Pour un thème marketplace, utilisez les balises <code className="font-mono text-foreground-700">marketplace_*</code>.</li>
        </ul>
      </div>
    </div>
  );
}